// lib/api-client.ts
import { authService } from './auth';
import axios, { AxiosInstance } from 'axios';
import {
    LoginCredentials,
    User, CreateUserDto, UpdateUserDto,
    Customer, CreatePbxHostDto, UpdatePbxHostDto,
    Widget, CreateWidgetDto,
    AppUser, CreateAppUserDto, UpdateAppUserDto,
    KamailioSubscriber, KamailioResponse,
    PbxHost, ValidateExtensionDto,
    PaginatedAppUserResponseDto,
    AppUserRole,
    FormFieldDto
} from '@/types';

const BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3350/v1';
const PBX_BASE_URL = process.env.NEXT_PUBLIC_PBX_API_URL || 'https://app.sipxentral.com/v1';

const addAuthInterceptors = (axiosInstance: AxiosInstance) => {
    // Request interceptor
    axiosInstance.interceptors.request.use((config) => {
        const token = authService.getAccessToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    });

    // Response interceptor
    axiosInstance.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error.config;

            if (error.response?.status === 401 && !originalRequest._retry) {
                originalRequest._retry = true;

                try {
                    await authService.refreshToken();
                    originalRequest.headers.Authorization = `Bearer ${authService.getAccessToken()}`;
                    return axiosInstance(originalRequest);
                } catch (refreshError) {
                    authService.logout();
                    window.location.href = '/login';
                    return Promise.reject(refreshError);
                }
            }

            return Promise.reject(error);
        }
    );
};

export const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const pbxApiClient = axios.create({
  baseURL: PBX_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth interceptors to both clients
addAuthInterceptors(apiClient);
addAuthInterceptors(pbxApiClient);

// API Functions
export const api = {
  auth: {
    login: async (credentials: Omit<LoginCredentials, 'userType'>) => {
      await authService.login(credentials.username, credentials.password);
      return {
        access_token: authService.getAccessToken()!,
        userType: 'platform'
    };
    },
  },
  
  users: {
    create: async (userData: CreateUserDto) => {
      const response = await apiClient.post('/users', userData);
      return response.data;
    },
    update: async (id: string, userData: UpdateUserDto) => {
      const response = await apiClient.patch(`/users/${id}`, userData);
      return response.data;
    },
    delete: async (id: string) => {
      const response = await apiClient.delete(`/users/${id}`);
      return response.data;
    },
    getAll: async (): Promise<User[]> => {
      const response = await apiClient.get('/users');
      return response.data;
    },
  },

  customers: {
    create: async (customerData: Omit<Customer, 'id'>) => {
      const response = await apiClient.post('/customers', customerData);
      return response.data;
    },
    getAll: async () => {
      const response = await apiClient.get('/customers');
      return response.data;
    },
    getOne: async (id: string) => {
      const response = await apiClient.get(`/customers/${id}`);
      return response.data;
    },
    update: async (id: string, customerData: Partial<Customer>) => {
      const response = await apiClient.patch(`/customers/${id}`, customerData);
      return response.data;
    },
    delete: async (id: string) => {
      const response = await apiClient.delete(`/customers/${id}`);
      return response.data;
    },
  },

  widgets: {
    create: async (widgetData: CreateWidgetDto) => {
      const response = await apiClient.post('/tokens', widgetData);
      return response.data;
    },
    getAll: async () => {
      const response = await apiClient.get('/widgets/all');
      return response.data;
    },
    getOne: async (shortToken: string) => {
      const response = await apiClient.get(`/widgets/${shortToken}`);
      return response.data;
    },
    getByCustomer: async (customerId: string) => {
      const response = await apiClient.get(`/widgets/customer/${customerId}`);
      return response.data;
    },
    update: async (shortToken: string, widgetData: Partial<Widget>) => {
      const response = await apiClient.patch(`/widgets/${shortToken}`, widgetData);
      return response.data;
    },
    delete: async (shortToken: string) => {
      const response = await apiClient.delete(`/widgets/${shortToken}`);
      return response.data;
    },
    uploadImage: async (shortToken: string, file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post(`/widgets/image/${shortToken}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
    updateFormSettings: async (shortToken: string, formSettings: FormFieldDto[]) => {
    const response = await apiClient.post(`/widgets/${shortToken}/form-settings`, {
      fields: formSettings
    });
    return response.data;
  },
    createBillingAccount: async (shortToken: string, data: { productId: number }) => {
      const response = await apiClient.post(`/widgets/${shortToken}/billing-account`, data);
      return response.data;
    },
    assignBillingAccount: async (shortToken: string, data: { billingAccountId: number }) => {
      const response = await apiClient.patch(`/widgets/${shortToken}/billing-account`, data);
      return response.data;
    },
  },

  kamailio: {
    create: async (subscriberData: KamailioSubscriber): Promise<KamailioResponse> => {
        const response = await apiClient.post('/kamailio', subscriberData)
        return response.data
    },
    get: async (username: string, domain: string): Promise<KamailioResponse> => {
        const response = await apiClient.get(`/kamailio/${username}/${domain}`)
        return response.data
    },
    update: async (username: string, domain: string, data: Partial<KamailioSubscriber>): Promise<KamailioResponse> => {
        const response = await apiClient.patch(`/kamailio/${username}/${domain}`, data)
        return response.data
    },
    delete: async (username: string, domain: string): Promise<KamailioResponse> => {
        const response = await apiClient.delete(`/kamailio/${username}/${domain}`)
        return response.data
    }
  },

  pbx: {
    create: async (data: CreatePbxHostDto): Promise<PbxHost> => {
      const response = await pbxApiClient.post('/asterisk', data);
      return response.data;
    },
    getAll: async (): Promise<PbxHost[]> => {
            const response = await pbxApiClient.get('/asterisk');
            return response.data.servers;
    },
    getOne: async (id: string): Promise<PbxHost> => {
      const response = await pbxApiClient.get(`/asterisk/${id}`);
      return response.data;
    },
    update: async (id: string, data: UpdatePbxHostDto): Promise<PbxHost> => {
      const response = await pbxApiClient.patch(`/asterisk/${id}`, data);
      return response.data;
    },
    delete: async (id: string): Promise<void> => {
      await pbxApiClient.delete(`/asterisk/${id}`);
    },
  },

  appUsers: {
    create: async (data: CreateAppUserDto): Promise<AppUser> => {
      const response = await pbxApiClient.post('/app-users', data);
      return response.data;
    },
    getAll: async ({ page = 1, limit = 10 } = {}): Promise<PaginatedAppUserResponseDto> => {
    const response = await pbxApiClient.get('/app-users', {
      params: { page, limit }
    });
    return response.data;
  },
    getOne: async (id: string): Promise<AppUser> => {
      const response = await pbxApiClient.get(`/app-users/${id}`);
      return response.data;
    },
    update: async (id: string, data: UpdateAppUserDto): Promise<AppUser> => {
      const response = await pbxApiClient.patch(`/app-users/${id}`, data);
      return response.data;
    },
    delete: async (id: string): Promise<void> => {
      await pbxApiClient.delete(`/app-users/${id}`);
    },
    validateExtension: async (data: ValidateExtensionDto): Promise<{ isValid: boolean; message?: string }> => {
      const response = await pbxApiClient.get('/app-users/validate-extension', { params: data });
      return response.data;
    },
    uploadAvatar: async (id: string, file: File): Promise<{ avatarPath: string }> => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await pbxApiClient.post(`/app-users/${id}/avatar`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
    deleteAvatar: async (id: string): Promise<{ message: string }> => {
      const response = await pbxApiClient.delete(`/app-users/${id}/avatar`);
      return response.data;
    },
    bulkUpdateServer: async (oldServerId: string, newServerId: string): Promise<{ message: string; updatedCount: number }> => {
      const response = await pbxApiClient.patch('/app-users/bulk-update-server', {
        oldServerId,
        newServerId,
      });
      return response.data;
    },
    updateServerHostname: async (serverId: string, newHostname: string): Promise<{ message: string; updatedCount: number }> => {
      const response = await pbxApiClient.patch('/app-users/update-server-hostname', {
        serverId,
        newHostname,
      });
      return response.data;
    },
    search: async (params: {
      customer?: string;
      server?: string;
      username?: string;
      name?: string;
      extension?: string;
      role?: AppUserRole;
      disabled?: boolean;
      page?: number;
      limit?: number;
      sort?: 'asc' | 'desc';
      sortBy?: 'createdAt' | 'username' | 'name' | 'extension';
    }): Promise<PaginatedAppUserResponseDto> => {
      const response = await pbxApiClient.get('/app-users/search', { params });
      return response.data;
    },
  },
  widgetSupervisors: {
    assign: async (widgetId: string, appUserId: string) => {
      const response = await pbxApiClient.post('/widget-supervisors', {
        widgetId,
        appUserId,
      });
      return response.data;
    },
    revoke: async (widgetId: string, appUserId: string) => {
      const response = await pbxApiClient.delete(`/widget-supervisors/${widgetId}/${appUserId}`);
      return response.data;
    },
    getWidgetSupervisors: async (widgetId: string) => {
      const response = await pbxApiClient.get(`/widget-supervisors/widget/${widgetId}`);
      return response.data;
    },
    getSupervisorWidgets: async (appUserId: string) => {
      const response = await pbxApiClient.get(`/widget-supervisors/supervisor/${appUserId}`);
      return response.data;
    },
  },
};